import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { I18N_KEYS } from '@/language/keys';
import Actions from '@/components/Menu/Actions';
import { useStickyNoteView } from '../FlowContext';
import { DEFAULT_STICKY_NOTE_COLOR, STICKY_NOTE_FONT_SIZE } from '../Views/StickyNoteView';
import ParentMenuItem from './ParentMenuItem';
import { ColorMenuItem } from './menu-items/ColorMenuItem';
import type { MenuAction } from '@/components/Menu/Actions';
import type { NodeId } from 'web';

interface StickyNoteContextMenuProps {
  positionX: number;
  positionY: number;
  isOpen: boolean;
  onClose: () => void;
  stickyNoteNodeId: NodeId;
}

export const StickyNoteContextMenu = ({
  positionX,
  positionY,
  isOpen,
  onClose,
  stickyNoteNodeId,
}: StickyNoteContextMenuProps) => {
  const { t } = useTranslation();
  const stickyNoteView = useStickyNoteView(stickyNoteNodeId);

  const handleColorChange = useCallback(
    (color: string) => {
      stickyNoteView.updateColor(color);
    },
    [stickyNoteView],
  );

  const handleFontSizeChange = useCallback(
    (fontSize: number) => {
      stickyNoteView.updateFontSize(fontSize);
      onClose();
    },
    [stickyNoteView, onClose],
  );

  const currentColor = stickyNoteView.getColor() || DEFAULT_STICKY_NOTE_COLOR;
  const currentFontSize = stickyNoteView.getFontSize() || STICKY_NOTE_FONT_SIZE.SMALL;

  const menu: MenuAction[] = useMemo(
    () => [
      {
        type: 'slot' as const,
        el: <ColorMenuItem currentColor={currentColor} onColorChange={handleColorChange} />,
      },
      {
        type: 'slot' as const,
        el: renderFontSizeMenuItem(t, handleFontSizeChange, currentFontSize),
      },
    ],
    [t, currentColor, handleColorChange, currentFontSize, handleFontSizeChange],
  );

  return (
    <AppContextMenu open={isOpen} onClose={onClose} mouseX={positionX} mouseY={positionY} items={menu} width="200px" />
  );
};

function renderFontSizeMenuItem(
  t: (key: string) => string,
  handleFontSizeChange: (fontSize: number) => void,
  currentFontSize: STICKY_NOTE_FONT_SIZE,
) {
  return (
    <ParentMenuItem
      label={t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE)}
      offset={{ left: '10px' }}
      contextMenuWidth="180px"
    >
      <Actions
        items={[
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.SMALL),
            action: () => handleFontSizeChange(STICKY_NOTE_FONT_SIZE.SMALL),
            selected: currentFontSize === STICKY_NOTE_FONT_SIZE.SMALL,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.MEDIUM),
            action: () => handleFontSizeChange(STICKY_NOTE_FONT_SIZE.MEDIUM),
            selected: currentFontSize === STICKY_NOTE_FONT_SIZE.MEDIUM,
          },
          {
            name: t(I18N_KEYS.RECIPE_MAIN.NODES.STICKY_NOTE.CONTEXT_MENU.FONT_SIZE_OPTIONS.LARGE),
            action: () => handleFontSizeChange(STICKY_NOTE_FONT_SIZE.LARGE),
            selected: currentFontSize === STICKY_NOTE_FONT_SIZE.LARGE,
          },
        ]}
      />
    </ParentMenuItem>
  );
}
